import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/**
 * AGM (Annual General Meeting) 전용 챗봇 플로우
 * Audited / Unaudited 두 코드(M32103 / M32014) 모두 같은 카테고리 "Annual General Meeting"
 * 따라서 단일 flow + panel을 공유하고, selection.documents 의 templateName 으로 폼 섹션을 토글한다.
 *
 * Task Title 입력 → T-Link entity 선택 → (우측 패널에서 변수 입력 + Create Task)
 */
export const agmFlow: ChatFlow = {
  id: 'agm',
  // ⚠ bare 'agm' 금지 — SG "AGM with Audited/Unaudited…" 카테고리를 가로채서
  //   SG 스튜디오 Venue 📍 가 빈 address-list 로 떨어진다.
  categoryMatch: ['annual general meeting'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}** (Annual General Meeting).\n\n` +
          `This preset includes ${docCount} document(s).\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for this AGM.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select Entity (Annual General Meeting).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Please fill the AGM details on the right panel and click **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    // ── Person Lookup steps (panel 의 AI 아이콘 클릭 시 진입) ──
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person details loaded. The form has been pre-filled. You can also drag/click the fields below.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    // ── Address chooser (member 등에 주소가 여러 개일 때) ──
    {
      id: 'address-list',
      aiMessage: 'This member has multiple addresses. Pick the one to use.',
      widget: 'address-options',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Enter the address. It will be filled into the form automatically.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    // 1) Task Title
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          {
            role: 'user',
            content: String(title),
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    // 2) Entity 선택
    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    // 3) AGM Panel 의 AI 아이콘 클릭 → 사람 lookup 진입
    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [{
            role: 'ai',
            content: 'Please select an entity first.',
            timestamp: Date.now(),
            stepId: 1,
            source: 'system',
          }],
        };
      }
      // defaultRole 이 지정되어 있으면 곧장 officer-list 로 진입
      if (ctx.defaultRole) {
        return {
          messages: [{
            role: 'user',
            content: `Looking up **${ctx.fieldGroup}** (role: ${ctx.defaultRole})...`,
            timestamp: Date.now(),
            stepId: 1,
          }],
          nextStepId: 'officer-list',
          nextStepWidgetData: {
            entityCd,
            selectedRole: ctx.defaultRole,
          },
        };
      }
      // 없으면 role 선택 단계
      return {
        messages: [{
          role: 'user',
          content: `Looking up **${ctx.fieldGroup}**...`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        nextStepId: 'officer-role',
      };
    }

    // 4) Officer Role 선택 → officer-list 단계
    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{
          role: 'user',
          content: `Role: **${role}**`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          entityCd,
          selectedRole: role,
        },
      };
    }

    // 5) Officer 선택 → officer-detail 단계 (chatBridge 가 detail fetch + selectedPerson 저장)
    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [{
          role: 'user',
          content: `Selected person: **${officerName}**`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        nextStepId: 'officer-detail',
      };
    }

    // 6) officer-list 에서 뒤로가기
    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    // 7) 주소 lookup — 목록이 비어 있거나 directInput 이면 직접 입력(빈 카드 방지)
    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = (Array.isArray(ctx.addresses) ? ctx.addresses : []).filter(
        (a: any) => a && String(a.value || '').trim(),
      );
      const label = ctx.subjectLabel || (ctx.fieldGroup === 'member' ? 'member' : 'this person');
      if (ctx.directInput || opts.length <= 1) {
        return {
          messages: [{
            role: 'user',
            content: `Enter **${label}** address.`,
            timestamp: Date.now(),
            stepId: 1,
          }],
          nextStepId: 'address-input',
        };
      }
      return {
        messages: [{
          role: 'user',
          content: `Choose an address for **${label}**.`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }
    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      const value = typeof payload === 'string' ? payload : payload?.value || payload?.address || '';
      if (!value || !String(value).trim()) return {};
      return {
        messages: [{ role: 'user', content: `Entered: **${String(value).trim()}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'confirm',
      };
    }

    // 8) 사용자가 address option 선택 → 다음 단계 없음 (chatBridge 가 form 채움)
    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [{
          role: 'user',
          content: `Selected: **${opt.label || opt.type}**`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        // confirm 단계로 복귀
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
